import React, { useState } from "react";
import ReadMore from "@/components/ReadMore";
import { useNavigate, useParams } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CNav,
  CNavItem,
  CNavLink,
  CRow,
  CSpinner,
  CTabContent,
  CTabPane,
} from "@coreui/react";
import { toast } from "react-toastify";

import ContentModerationTabs from "@/components/contentModeration/ContentModerationTabs";
import ModerationStatusBadge from "@/components/contentModeration/ModerationStatusBadge";
import {
  showConfirmDialog,
  showDeactivateDialog,
} from "@/components/contentModeration/moderationDialogs";
import {
  CreatorCell,
  NO_VALUE,
  SECTIONS,
  Thumbnail,
} from "@/components/contentModeration/sections";
import { useTranslationContext } from "@/context/TranslationContext";
import { contentModeration as tr } from "@/constants/translation/contentModeration.translation";
import { dateTimeFormat } from "@/utils/dateFormate";
import {
  useContentModerationItem,
  useDeactivateContent,
  useDeleteContent,
  useReactivateContent,
} from "@/hooks/useContentModeration";

const TABS = { DETAILS: "details" };
// Long free text (description, post text, deactivation reason) collapses behind Read more / Show less
const LONG_TEXT_LENGTH = 200;

const DetailRow = ({ label, children, full = false }) => (
  <CCol md={full ? 12 : 6} className="mb-3">
    <div className="text-muted small">{label}</div>
    <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {children}
    </div>
  </CCol>
);

const hasValue = (value) =>
  value !== undefined && value !== null && value !== "";

/**
 * Generic Content Moderation view page - basic information only (no members, comments or likes).
 *
 * The body is a tab set driven by `tabs` below, so a "Comments" / "Likes" tab can be added later by adding one
 * entry to that array, without touching the rest of the page.
 *
 * @param {Object} props
 * @param {string} props.sectionKey - groups | communities | activities | feedPosts
 */
const ContentModerationView = ({ sectionKey }) => {
  const t = useTranslationContext();
  const navigate = useNavigate();
  const { id } = useParams();
  const section = SECTIONS[sectionKey];
  const [activeTab, setActiveTab] = useState(TABS.DETAILS);

  const {
    data: response,
    isLoading,
    isError,
  } = useContentModerationItem(sectionKey, id);
  const data = response?.data;

  const onFailure = (error) =>
    toast.error(error?.response?.data?.message || t(tr.toast.failed));
  const { mutate: deactivate } = useDeactivateContent(
    sectionKey,
    (res) => toast.success(res?.message || t(tr.toast.deactivated)),
    onFailure,
  );
  const { mutate: reactivate } = useReactivateContent(
    sectionKey,
    (res) => toast.success(res?.message || t(tr.toast.reactivated)),
    onFailure,
  );
  const { mutate: remove } = useDeleteContent(
    sectionKey,
    (res) => {
      toast.success(res?.message || t(tr.toast.deleted));
      navigate(section.routes.list);
    },
    onFailure,
  );

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <CSpinner />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <CCard>
        <CCardBody className="text-center py-5">
          <p>{t(tr.view.not_found)}</p>
          <CButton
            color="primary"
            onClick={() => navigate(section.routes.list)}
          >
            {t(tr.view.back)}
          </CButton>
        </CCardBody>
      </CCard>
    );
  }

  const isDeactivated = data.moderationStatus === "deactivated";
  const fields = section.fields(data, t);

  const moderationFields = isDeactivated
    ? [
        { label: tr.view.fields.reason, value: data.deactivationReason },
        {
          label: tr.view.fields.deactivated_at,
          value: dateTimeFormat(data.deactivatedAt),
        },
        { label: tr.view.fields.deactivated_by, value: data.deactivatedBy },
      ]
    : [];

  const renderDetails = () => (
    <CRow>
      <DetailRow label={t(tr.view.fields.creator)}>
        <CreatorCell creator={data.creator} />
      </DetailRow>
      {[...fields, ...moderationFields].map((field) => (
        <DetailRow key={field.label} label={t(field.label)} full={field.full}>
          {!hasValue(field.value) ? (
            NO_VALUE
          ) : typeof field.value === "string" ? (
            <ReadMore maxLength={LONG_TEXT_LENGTH}>{field.value}</ReadMore>
          ) : (
            field.value
          )}
        </DetailRow>
      ))}
    </CRow>
  );

  // Add { key: "comments", label: ..., render: ... } / likes here later - nothing else needs to change.
  const tabs = [
    { key: TABS.DETAILS, label: tr.view.tabs.details, render: renderDetails },
  ];

  return (
    <div className="content-moderation-view">
      <ContentModerationTabs />
      <CCard className="mb-4">
        <CCardBody>
          <div className="d-flex flex-wrap gap-3 justify-content-between align-items-start">
            <div className="d-flex gap-3 align-items-center">
              <Thumbnail
                src={section.getImage(data)}
                size={96}
                previewable
                title={section.getTitle(data)}
              />
              <div>
                <h4 className="mb-1" style={{ wordBreak: "break-word" }}>
                  {section.getTitle(data) || NO_VALUE}
                </h4>
                <ModerationStatusBadge status={data.moderationStatus} />
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {isDeactivated ? (
                <CButton
                  color="success"
                  className="moderation-action-btn"
                  onClick={() =>
                    showConfirmDialog({
                      t,
                      texts: tr.popups.reactivate,
                      onConfirm: () => reactivate(data.uuid),
                    })
                  }
                >
                  {t(tr.actions.reactivate)}
                </CButton>
              ) : (
                <CButton
                  color="warning"
                  className="moderation-action-btn"
                  onClick={() =>
                    showDeactivateDialog({
                      t,
                      onConfirm: (reason) =>
                        deactivate({ uuid: data.uuid, reason }),
                    })
                  }
                >
                  {t(tr.actions.deactivate)}
                </CButton>
              )}
              <CButton
                color="danger"
                className="moderation-action-btn"
                onClick={() =>
                  showConfirmDialog({
                    t,
                    texts: tr.popups.delete,
                    onConfirm: () => remove(data.uuid),
                  })
                }
              >
                {t(tr.actions.delete)}
              </CButton>
              <CButton
                color="secondary"
                className="moderation-action-btn"
                onClick={() => navigate(section.routes.list)}
              >
                {t(tr.view.back)}
              </CButton>
            </div>
          </div>
        </CCardBody>
      </CCard>

      <CCard>
        <CCardBody>
          <CNav variant="tabs" className="mb-4 view-user-tabs">
            {tabs.map((tab) => (
              <CNavItem key={tab.key}>
                <CNavLink
                  href="#"
                  active={activeTab === tab.key}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab(tab.key);
                  }}
                >
                  {t(tab.label)}
                </CNavLink>
              </CNavItem>
            ))}
          </CNav>
          <CTabContent>
            {tabs.map((tab) => (
              <CTabPane key={tab.key} visible={activeTab === tab.key}>
                {tab.render()}
              </CTabPane>
            ))}
          </CTabContent>
        </CCardBody>
      </CCard>
    </div>
  );
};

export default ContentModerationView;
